import type { Ref, RefObject } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import type { TimeSlot } from '../useDateTimePicker.js';
import { CalendarGrid } from './CalendarGrid.js';
import { TimeSelect } from './TimeSelect.js';

/** Matches `sm:mt-1` / `sm:mb-1` — the gap between the trigger and the popover. */
const POPOVER_GAP = 4;
/** Tailwind's `sm` breakpoint. Below it the panel is a fixed bottom sheet and cannot spill. */
const DESKTOP = '(min-width: 640px)';
/** Breathing room kept between the desktop popover and either edge of the viewport. */
const VIEWPORT_MARGIN = 8;

interface PickerPanelProps {
  panelRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  viewYear: number;
  viewMonth: number;
  pendingYMD: string | null;
  pendingHour: number;
  pendingMinute: number;
  timeSlots: TimeSlot[];
  minDate?: Date;
  maxDate?: Date;
  onSelectDay: (ymd: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectTime: (hour: number, minute: number) => void;
  canConfirm: boolean;
  onConfirm: () => void;
}

// Popover shell anchored under the trigger; a mobile backdrop taps to close.
// panelRef is attached to the content div so outside-click excludes only it.
export function PickerPanel({
  panelRef,
  triggerRef,
  viewYear,
  viewMonth,
  pendingYMD,
  pendingHour,
  pendingMinute,
  timeSlots,
  minDate,
  maxDate,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
  onSelectTime,
  canConfirm,
  onConfirm,
}: PickerPanelProps) {
  /**
   * 093 FR-021: the desktop popover is `sm:top-full`, so it always opened downward and
   * could cross the fold — measured 22px on `main`, and 62px once the wheel made the
   * panel taller. Flip it above the trigger when opening downward would spill.
   *
   * 🔴 The rule is "flip only if flipping FITS", not "flip whenever the bottom
   * overflows": a trigger near the TOP of the viewport would otherwise trade a clipped
   * bottom for a clipped top, which is the same defect upside down.
   *
   * Capping the panel's height was rejected — it would introduce internal scrolling on
   * the one variant that has never had it (contract check 21).
   */
  const [flipUp, setFlipUp] = useState(false);
  const [shiftY, setShiftY] = useState(0);

  // No dependency array on purpose: the sheet resizes live when the month changes
  // (measured 576 <-> 614), so the decision has to be re-taken on every render. It
  // cannot oscillate — every input (the TRIGGER's rect, the panel's HEIGHT, the
  // viewport) is independent of where the panel is currently placed, and both
  // setState calls bail when the value is unchanged.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger || typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia(DESKTOP).matches) {
      setFlipUp(false);
      setShiftY(0);
      return;
    }

    const t = trigger.getBoundingClientRect();
    const height = panel.offsetHeight;
    const viewport = window.innerHeight;

    // Where each placement WOULD put the panel's top, in viewport coordinates.
    const topIfBelow = t.bottom + POPOVER_GAP;
    const topIfAbove = t.top - POPOVER_GAP - height;
    const fitsBelow = topIfBelow + height <= viewport - VIEWPORT_MARGIN;
    const fitsAbove = topIfAbove >= VIEWPORT_MARGIN;

    // Flip only if flipping actually FITS — otherwise a trigger near the top of the
    // viewport trades a clipped bottom for a clipped top, the same defect upside down.
    const flip = !fitsBelow && fitsAbove;
    const anchorTop = flip ? topIfAbove : topIfBelow;

    // 🔴 Flipping alone cannot satisfy FR-021, and never could — this panel is far
    // taller than the room above a trigger near the top of the page. Measured at
    // 1024x768 with the trigger at y=150: the panel is 630px, so it spills 62px below
    // and would clip 484px above; on `main` (590px, no wheel) it spilled the 22px the
    // spec recorded and would still have clipped 444px above. So the popover is also
    // SHIFTED back inside the viewport when neither side fits. Shifting is the one
    // remedy left: capping the height is forbidden (contract check 21 — it would
    // introduce internal scrolling on the one variant that has never had it), and
    // letting it spill is the defect.
    const highestTop = VIEWPORT_MARGIN;
    const lowestTop = Math.max(VIEWPORT_MARGIN, viewport - VIEWPORT_MARGIN - height);
    const clampedTop = Math.min(Math.max(anchorTop, highestTop), lowestTop);

    setFlipUp(flip);
    setShiftY(Math.round(clampedTop - anchorTop));
  });

  return (
    <>
      {/* Decorative mobile dim; tapping it lands outside the panel, so the
          hook's outside-click handler closes the picker (no handler needed here). */}
      <div className="fixed inset-0 bg-black/50 z-40 sm:hidden" aria-hidden="true" />

      <div
        ref={panelRef as unknown as Ref<HTMLDivElement>}
        // The shift is a transform rather than a `top`, so it moves the popover
        // without disturbing the layout the anchor classes establish — and it is
        // always 0 on mobile, where the sheet is fixed to the bottom edge.
        style={shiftY === 0 ? undefined : { transform: `translateY(${shiftY}px)` }}
        className={[
          // z-[60] at EVERY width: BottomNav is `fixed bottom-0 ... z-50` with no `sm:`
          // guard and comes AFTER this sheet in the DOM, so at an equal z-index it paints
          // on top and swallows the tap. 092 fixed that on mobile and left desktop tied at
          // 50 to keep itself small; 093 FR-020 takes the other half. Measured at
          // 1024x768 before this line changed: the point over Confirm did not belong to
          // Confirm.
          'bg-surface-card border border-border z-[60] px-4 pt-4 flex flex-col',
          'fixed bottom-0 left-0 right-0 rounded-t-2xl max-h-[90vh]',
          'sm:absolute sm:inset-auto sm:left-0 sm:w-72',
          flipUp ? 'sm:bottom-full sm:mb-1' : 'sm:top-full sm:mt-1',
          'sm:rounded-2xl sm:shadow-xl sm:max-h-none',
        ].join(' ')}
      >
        <div className="w-10 h-1 bg-border rounded-full mx-auto mb-3 shrink-0 sm:hidden" />

        {/* The body owns the scroll; the sheet above owns the cap. min-h-0 is not
            decoration — without it this flex child refuses to shrink below its
            content and pushes the footer back out of the sheet. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <CalendarGrid
            year={viewYear}
            month={viewMonth}
            selectedYMD={pendingYMD}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDay={onSelectDay}
            onPrevMonth={onPrevMonth}
            onNextMonth={onNextMonth}
          />

          <TimeSelect
            slots={timeSlots}
            selectedHour={pendingHour}
            selectedMinute={pendingMinute}
            onSelect={onSelectTime}
          />
        </div>

        {/* The footer must stay a descendant of the ref'd sheet: outside-click
            dismissal keys off panelRef, so a footer lifted out of it would close
            the panel on mousedown, before onConfirm could run. The bottom padding
            that keeps the button clear of a device's gesture area lives here too:
            left on the sheet it would sit below the footer as dead space. */}
        <div className="shrink-0 pb-6 sm:pb-4">
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="mt-4 w-full h-10 rounded-xl bg-primary text-on-primary font-semibold text-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
        </div>
      </div>
    </>
  );
}
